import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Link,
  useTheme
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Security,
  Storage,
  Visibility,
  Shield,
  Lock,
  CloudQueue,
  DeleteForever
} from '@mui/icons-material';

const Privacy = () => {
  const theme = useTheme();

  const whatWeCollect = [
    {
      title: 'Anonymous Usage Statistics',
      description: 'We track how many diagrams are generated and which features are used (Applications vs Profiles, Live vs Offline mode).'
    },
    {
      title: 'Organisation Count (Hashed)',
      description: 'Your tenant ID is passed through a one-way SHA-256 hash to count unique organisations. This hash cannot be reversed.'
    }
  ];

  const whatWeDontCollect = [
    {
      title: 'No Personal Information',
      description: 'We never permanently store your name, email, or any personal identifiers.'
    },
    {
      title: 'No Organisation Identifiers',
      description: 'We never store organisation names or tenant IDs in plain text.'
    },
    {
      title: 'No Policy or Application Data',
      description: 'Your Intune data is processed temporarily in memory and never permanently stored.'
    },
    {
      title: 'No Third-Party Analytics',
      description: 'We do not use Google Analytics, tracking pixels, or any third-party analytics services.'
    }
  ];

  const dataProcessing = [
    {
      icon: <CloudQueue />,
      title: 'Data Fetching',
      description: 'IntuneMermaid uses your access token to fetch data directly from Microsoft Graph API. We request read-only access to your Intune apps, configuration profiles, and group memberships.'
    },
    {
      icon: <Storage />,
      title: 'Processing in Memory',
      description: 'Your data is processed entirely in server memory (RAM). Temporary files created during processing are automatically deleted immediately after diagram generation.'
    },
    {
      icon: <DeleteForever />,
      title: 'Automatic Cleanup',
      description: 'Once your diagram is generated, all policy and application data is discarded from memory. Any remaining temporary files are cleaned up within 1 hour.'
    },
    {
      icon: <Lock />,
      title: 'Encrypted in Transit',
      description: 'All communications use HTTPS encryption to protect your data while it travels between your browser and our servers.'
    }
  ];

  const permissions = [
    
    {
      permission: 'DeviceManagementApps.Read.All',
      description: 'Read Intune applications and their assignments'
    },
    {
      permission: 'DeviceManagementConfiguration.Read.All',
      description: 'Read Intune configuration profiles and their assignments'
    },
    {
      permission: 'GroupMember.Read.All',
      description: 'Read group names to display assignment information'
    },
    {
      permission: 'DeviceManagementRBAC.Read.All',
      description: 'Read Intune scope tags for filtering diagrams by scope'
    }

  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Privacy & Data Collection
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Your privacy is our priority. Here's exactly what data we collect and how we handle it.
        </Typography>
      </Box>

      {/* What We Collect */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Visibility sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              What We Collect
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            We collect minimal, anonymized usage statistics to understand how IntuneMermaid is being used:
          </Typography>
          <Grid container spacing={2}>
            {whatWeCollect.map((item, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper
                  sx={{
                    p: 2,
                    height: '100%',
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                    <CheckCircle color="success" sx={{ mr: 1, mt: 0.3 }} fontSize="small" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {item.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {item.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* What We DON'T Collect */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Shield sx={{ mr: 1, color: 'success.main' }} />
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              What We DON'T Collect
            </Typography>
          </Box>
          <List dense>
            {whatWeDontCollect.map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Cancel color="error" />
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  secondary={item.description}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* How Data is Processed */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Storage sx={{ mr: 1, color: 'info.main' }} />
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              How Your Data Is Processed
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {dataProcessing.map((item, index) => (
              <Grid item xs={12} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'primary.50',
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {React.cloneElement(item.icon, { color: 'primary' })}
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </Box>
                </Box>
                {index < dataProcessing.length - 1 && <Divider sx={{ my: 2 }} />}
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Microsoft Entra ID Authentication */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Lock sx={{ mr: 1, color: 'warning.main' }} />
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              Microsoft Entra ID Authentication
            </Typography>
          </Box>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="Authentication is handled directly by Microsoft's secure infrastructure" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="Your credentials are never sent to or stored by IntuneMermaid" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="All permissions are read-only — we cannot modify your Intune configuration" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="You can revoke access at any time through your Microsoft Entra admin portal" />
            </ListItem>
          </List>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Required Permissions
          </Typography>
          <Grid container spacing={2}>
            {permissions.map((item, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50'
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: 'monospace',
                      bgcolor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1
                    }}
                  >
                    {item.permission}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {item.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Offline Mode */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Security sx={{ mr: 1, color: 'info.main' }} />
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              Offline Mode
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            IntuneMermaid supports offline diagram generation where you can upload your own JSON exports:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="Uploaded files are processed in memory and immediately deleted" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="No authentication required for offline mode" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="No tenant information is tracked for offline diagrams" />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Contact */}
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="h6" gutterBottom>
          Questions or Concerns?
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          If you have any questions about our privacy practices, please reach out:
        </Typography>
        <Link
          href="https://github.com/schenardie"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ mr: 2 }}
        >
          Jose Schenardie
        </Link>
      </Box>
    </Container>
  );
};

export default Privacy;
